import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { webhookReceiptsApi, type OrphanReceipt, type ReplayResult } from '@/lib/api';
import { cn } from '@/lib/utils';

function platformBadge(platform: string) {
  const map: Record<string, string> = {
    justt: 'bg-purple-100 text-purple-700',
    midigator: 'bg-blue-100 text-blue-700',
    kount_dcm: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold font-mono', map[platform] ?? 'bg-slate-100 text-slate-600')}>
      {platform}
    </span>
  );
}

function timeAgo(unixSec: number): string {
  const diff = Math.floor(Date.now() / 1000 - unixSec);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ReplayResultPanel({ result }: { result: ReplayResult }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-3 mt-3 text-[12px] space-y-1">
      <p className="font-semibold text-slate-700">
        Replay: {result.eventsReplayed} event{result.eventsReplayed === 1 ? '' : 's'} processed
      </p>
      <ul className="space-y-0.5">
        {result.results.map((r, i) => {
          const isOk = r.action === 'outcome_applied';
          const isOrphan = r.action === 'still_orphan';
          return (
            <li key={i} className={cn('flex items-center gap-2 font-mono text-[11px]', isOk ? 'text-emerald-700' : isOrphan ? 'text-amber-700' : 'text-red-600')}>
              <span>•</span>
              <span className="font-semibold">{r.action}</span>
              {r.caseId && <span className="text-slate-500">→ {r.caseId}</span>}
              {r.error && <span className="text-slate-500">— {r.error}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function OrphanRow({ receipt, lastResult, onReplay, isReplaying }: {
  receipt: OrphanReceipt;
  lastResult?: ReplayResult;
  onReplay: () => void;
  isReplaying: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const summary = receipt.rawPayload as Record<string, unknown> | undefined;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-sm transition">
      <div className="flex items-center gap-3 flex-wrap">
        {platformBadge(receipt.platform)}
        <span className="font-mono text-[12px] font-semibold text-slate-700">{receipt.eventType}</span>
        {receipt.companyId && (
          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-mono">{receipt.companyId}</span>
        )}
        <span className="text-[11px] text-slate-400 font-mono">{timeAgo(receipt.receivedAt)}</span>
        <span className="flex-1" />
        <button
          className={cn(
            'rounded px-3 py-1 text-[12px] font-semibold transition',
            isReplaying
              ? 'bg-slate-200 text-slate-400 cursor-wait'
              : 'bg-slate-900 text-white hover:bg-slate-800',
          )}
          onClick={onReplay}
          disabled={isReplaying}
        >
          {isReplaying ? 'Replaying…' : 'Replay'}
        </button>
        <button
          className="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide payload' : 'Show payload'}
        </button>
      </div>
      <p className="mt-2 text-[12px] text-slate-500">{receipt.error}</p>
      <p className="mt-1 text-[11px] text-slate-400 font-mono break-all">eventId: {receipt.eventId} • receiptId: {receipt.receiptId}</p>
      {expanded && summary && (
        <pre className="mt-3 rounded bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono text-slate-700 overflow-x-auto max-h-72">
          {JSON.stringify(summary, null, 2)}
        </pre>
      )}
      {lastResult && <ReplayResultPanel result={lastResult} />}
    </div>
  );
}

function WebhookReceiptsPage() {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState<string>('');
  const [replayResults, setReplayResults] = useState<Record<string, ReplayResult>>({});

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-orphan-receipts', companyId],
    queryFn: () => webhookReceiptsApi.listOrphans(companyId || undefined, 100),
  });

  const replayMutation = useMutation({
    mutationFn: (receiptId: string) => webhookReceiptsApi.replay(receiptId),
    onSuccess: (response, receiptId) => {
      if (response.data) {
        setReplayResults((prev) => ({ ...prev, [receiptId]: response.data! }));
      }
      // Refresh the orphan list so successfully replayed receipts drop off
      queryClient.invalidateQueries({ queryKey: ['admin-orphan-receipts'] });
    },
  });

  const receipts = data?.data?.receipts ?? [];
  const groupedByPlatform = receipts.reduce<Record<string, OrphanReceipt[]>>((acc, r) => {
    (acc[r.platform] = acc[r.platform] ?? []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Webhook Receipts</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Orphan outcome events — webhooks that arrived before the case existed and were silently dropped.
            Replay re-applies them now that the case may have been created.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by companyId (optional)"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="rounded border border-slate-200 px-3 py-1.5 text-[12px] font-mono w-64"
          />
          <button
            onClick={() => refetch()}
            className={cn(
              'rounded border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50',
              isFetching && 'opacity-60',
            )}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-700">
          {(error as Error).message ?? 'Failed to load orphan receipts'}
        </div>
      )}

      {isLoading && <p className="text-[13px] text-slate-400">Loading…</p>}

      {!isLoading && receipts.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
          <p className="text-[14px] font-semibold text-slate-700">No orphan outcomes</p>
          <p className="mt-1 text-[12px] text-slate-500">
            Every received outcome event either matched a case or has not yet entered the skip queue.
          </p>
        </div>
      )}

      {Object.entries(groupedByPlatform).map(([platform, list]) => (
        <section key={platform} className="space-y-2">
          <div className="flex items-baseline gap-2">
            {platformBadge(platform)}
            <span className="text-[12px] text-slate-500 font-mono">{list.length} orphan{list.length === 1 ? '' : 's'}</span>
          </div>
          <div className="space-y-2">
            {list.map((r) => (
              <OrphanRow
                key={r.receiptId}
                receipt={r}
                lastResult={replayResults[r.receiptId]}
                isReplaying={replayMutation.isPending && replayMutation.variables === r.receiptId}
                onReplay={() => replayMutation.mutate(r.receiptId)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/webhook-receipts')({
  component: WebhookReceiptsPage,
});
